import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { QuotationDetailDto, downloadQuotationClientPDF, downloadQuotationClientExcel, quotationDownloadErrorMessage } from '../lib/quotationApi';
import ExportDropdown from './ExportDropdown';
import Button from './ui/Button';
import { useDialogDismiss } from './ui/useDialogDismiss';
import { formatDate as fmtDate } from "../utils/dateFormat";
import { resolveItemPricing, resolveQuotationTotals, DEFAULT_MARKUP_PERCENT, QuotationTotals } from '../utils/pricing';
// What a CLIENT is allowed to see — the one owner, shared with
// `src/services/quotationExport.service.js` through its mirrored twin. This
// component used to keep its own column set and its own summary rows, and they
// drifted the dangerous way: the preview showed a Profit row and a More Details
// column that no client export sends, on the screen with the SEND button on it
// (ClickUp 86d478wad). Do not re-list columns here — add them to the owner.
import { clientVisibleItemFields, clientVisibleSummaryRows } from '../utils/clientVisibleFields';
import { currencyPrefix } from '../utils/priceUtils';

// What the client PDF prints for a money cell, character for character:
// `quotationExport.service.js` writes `US$${x.toFixed(2)}` (TECH_DEBT #220) —
// no grouping, rounded not ceiled — so not `formatCurrency`, which groups and
// ceils and could differ from the PDF by a comma or a cent. The label is the
// owner's (BUSINESS_RULES#quotation-screen-money-labels): "US$", never "$".
const pdfMoney = (amount: number): string => `${currencyPrefix('USD')}${amount.toFixed(2)}`;

interface ClientViewPopupProps {
  quotation: QuotationDetailDto;
  /**
   * The vessel line: the page's answer (D104.12 - the quotation's own copy, else the RFQ's), the one its
   * header shows and the PDF the client receives prints. Null when nothing names one ("N/A"). The popup
   * does not work it out: it used to read the quotation's own copy alone, and a ship only the RFQ names
   * read "N/A" on the screen with the SEND button on it.
   */
  vesselName: string | null;
  isOpen: boolean;
  onClose: () => void;
  discountPercentage?: number;
  /**
   * The page's markup, NOT the house default. This popup called
   * `resolveItemPricing(item)` with no rate, so a line with no stored selling
   * price was priced here at 30% while the grid behind it used whatever rate the
   * operator had applied — two prices for one line, and the client-facing one was
   * the wrong one.
   */
  markupPercentage?: number;
  /**
   * The quotation's totals, computed once by the page. Passed rather than
   * recomputed: this component had its own `selling - cost` profit and a Final
   * Price that omitted transportation and the other charges, so it quoted a
   * total the page it was opened from did not agree with.
   */
  totals?: QuotationTotals;
  onSendClick?: () => void;
}

const ClientViewPopup: React.FC<ClientViewPopupProps> = ({ quotation, vesselName, isOpen, onClose, discountPercentage = 0, markupPercentage = DEFAULT_MARKUP_PERCENT, totals, onSendClick }) => {
  const [downloading, setDownloading] = useState(false);
  // Escape and a backdrop click close it, as they close every other dialog (B019). Called before
  // the early return below: a hook may not be skipped while the popup is closed.
  const dismiss = useDialogDismiss(isOpen, onClose);

  if (!isOpen) return null;

  const formatDate = (dateString: string) => {
    return fmtDate(dateString);
  };

  const handleClientDownloadPDF = async () => {
    try {
      setDownloading(true);
      await downloadQuotationClientPDF(quotation._id, discountPercentage);
    } catch (err: any) {
      console.error('Failed to download Client PDF:', err);
      // Worded from the server's CODE, never its sentence (invariant 6).
      alert(quotationDownloadErrorMessage(err, 'Client PDF'));
    } finally {
      setDownloading(false);
    }
  };

  const handleClientDownloadExcel = async () => {
    try {
      setDownloading(true);
      await downloadQuotationClientExcel(quotation._id, discountPercentage);
    } catch (err: any) {
      console.error('Failed to download Client Excel:', err);
      // Worded from the server's CODE, never its sentence (invariant 6).
      alert(quotationDownloadErrorMessage(err, 'Client Excel'));
    } finally {
      setDownloading(false);
    }
  };

  // Selling prices for the client view. Items whose price was never adjusted may have
  // no stored unitSellingPrice/totalSellingPrice — without a fallback those rows render
  // as $0.00 and look "missing" to the client.
  //
  // Through the owner (`utils/pricing`), not a local rule. This spelled out
  // `cost * (1 + markup / 100)` with its own `DEFAULT_MARKUP = 30` beside it,
  // which made the house rate a number written down in three places on the
  // client, and — because it did not round at all where every other path ceils
  // to the next cent — it could show the client a price BELOW the quotation and
  // the PDF this very popup downloads. (TECH_DEBT #146.)
  const sellingFor = (item: QuotationDetailDto['items'][number]) => {
    const { unitSellingPrice, totalSellingPrice } = resolveItemPricing(item, markupPercentage);
    return { unit: unitSellingPrice, total: totalSellingPrice };
  };

  // The page's own totals when it passed them; otherwise the same owner, same
  // rules — never a local `selling - cost` again. Profit is AFTER the discount and
  // Final Price carries the charges, so this popup and the screen behind it show
  // one quote rather than two.
  const summary =
    totals ??
    resolveQuotationTotals(quotation.items, {
      defaultMarkupPercent: markupPercentage,
      discountPercent: discountPercentage,
    });
  const totalSellingPrice = summary.sellingBeforeDiscount;
  const finalPrice = summary.finalPrice;

  // Grid track and rendering per owner-declared column. A field the owner adds
  // that this table has never heard of still renders, at the default track —
  // never silently dropped, which is the failure this whole change is about.
  // `wrap`: the PDF the client receives wraps the item name and the remarks inside their column
  // (D105; the remarks since 2026-09-26), so the preview shows them in full too, not cut with an ellipsis.
  const COLUMN_LAYOUT: Record<string, { track: string; truncate?: boolean; wrap?: boolean }> = {
    lineNumber: { track: '48px' },
    itemName: { track: 'minmax(180px, 1fr)', wrap: true },
    quantity: { track: '70px' },
    uom: { track: '70px' },
    unitSellingPrice: { track: '90px' },
    totalSellingPrice: { track: '90px' },
    remarks: { track: 'minmax(140px, 1fr)', wrap: true },
  };
  const DEFAULT_COLUMN_LAYOUT = { track: 'minmax(140px, 1fr)', truncate: true };

  const itemColumns = clientVisibleItemFields().map((field) => ({
    ...field,
    ...(COLUMN_LAYOUT[field.key] || DEFAULT_COLUMN_LAYOUT),
  }));

  const cellText = (
    key: string,
    item: QuotationDetailDto['items'][number],
    index: number,
    rowUnitSelling: number,
    rowTotalSelling: number
  ): string => {
    switch (key) {
      case 'lineNumber': return String(index + 1);
      case 'itemName': return item.itemName || '';
      case 'quantity': return String(item.quantity ?? '');
      case 'uom': return item.uom || 'PCE';
      case 'unitSellingPrice': return pdfMoney(rowUnitSelling);
      case 'totalSellingPrice': return pdfMoney(rowTotalSelling);
      case 'remarks': return item.remarks || '';
      default: return '';
    }
  };

  // Summary rows, from the same owner. Profit used to be one of these and is
  // not client-visible: no client export has ever emitted it.
  const summaryRows = clientVisibleSummaryRows().map((row) => ({
    ...row,
    value:
      row.key === 'totalPrice'
        ? pdfMoney(totalSellingPrice)
        : row.key === 'discount'
        ? `${discountPercentage}%`
        : row.key === 'finalPrice'
        ? pdfMoney(finalPrice)
        : '',
    emphasis: row.key === 'finalPrice',
  }));

  const content = (
    <div
      data-testid="client-view-backdrop"
      onMouseDown={dismiss.onMouseDown}
      onClick={dismiss.onClick}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 modal-overlay-safe"
    >
      {/* Modal Container - flex layout to avoid overlap */}
      <div className="bg-white rounded-lg shadow-xl flex flex-col w-full min-w-[900px] max-w-[95vw] h-[85vh] max-h-[95vh] overflow-hidden">
        {/* Header Bar */}
        <div className="flex-shrink-0 flex items-start justify-between px-6 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="text-base font-semibold leading-6 text-slate-900">
              RFQ: {quotation.rfqNumber || 'N/A'}
            </h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-500">
                Client: {quotation.customer?.customerName || 'N/A'}
              </span>
              <span className="font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-500">|</span>
              <span className="font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-500">
                Vessel: {vesselName || 'N/A'}
              </span>
              <span className="font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-500">|</span>
              <span className="font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-500">
                Created: {quotation.createdAt ? formatDate(quotation.createdAt) : 'N/A'}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded hover:bg-gray-300 flex items-center justify-center text-gray-600 transition-colors text-lg leading-none"
          >
            ×
          </button>
        </div>

        {/* Main Content - flex row: table + summary */}
        <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* Table Container */}
          <div className="flex-1 min-w-0 overflow-auto p-4 pr-2">
            <div
              className="grid border border-[#D7DBD9] rounded-md overflow-hidden"
              style={{
                gridTemplateColumns: itemColumns.map((c) => c.track).join(' '),
                gridAutoRows: 'min-content',
                fontFamily: 'Public Sans, sans-serif'
              }}
            >
              {/* Header Row — captions from the owner, so this preview cannot name
                  a column differently from the file the client receives. */}
              {itemColumns.map((column, columnIndex) => (
                <div
                  key={column.key}
                  className={`bg-[#F9FAF9] flex items-center justify-center py-[3px] px-[8px] ${
                    columnIndex === itemColumns.length - 1 ? '' : 'border-r border-[#D7DBD9]'
                  }`}
                >
                  <span className="font-['Public_Sans'] font-medium text-xs leading-[18px] text-slate-900">
                    {column.shortLabel}
                  </span>
                </div>
              ))}

              {/* Data Rows */}
              {quotation.items.map((item, index) => {
                const { unit: rowUnitSelling, total: rowTotalSelling } = sellingFor(item);
                return (
                <React.Fragment key={index}>
                  {itemColumns.map((column, columnIndex) => {
                    const text = cellText(column.key, item, index, rowUnitSelling, rowTotalSelling);
                    const isLast = columnIndex === itemColumns.length - 1;
                    return (
                      <div
                        key={column.key}
                        className={`bg-white border-t border-[#D7DBD9] flex items-center py-[4px] px-[8px] ${
                          isLast ? '' : 'border-r'
                        } ${column.truncate || column.wrap ? 'min-w-0 overflow-hidden' : 'justify-center'}`}
                      >
                        <span
                          className={`font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-900 ${
                            column.wrap
                              ? 'text-left min-w-0 block whitespace-normal break-words'
                              : column.truncate
                                ? 'truncate text-left min-w-0 block'
                                : ''
                          }`}
                          title={column.truncate ? text : undefined}
                        >
                          {text}
                        </span>
                      </div>
                    );
                  })}
                </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Summary + Buttons - right side */}
          <div className="flex-shrink-0 w-[280px] p-4 pl-2 flex flex-col gap-4 border-l border-slate-200">
            <div className="grid border border-[#D7DBD9] rounded-md overflow-hidden" style={{ gridTemplateColumns: '1fr 1fr' }}>
              {summaryRows.map((row, rowIndex) => (
                <React.Fragment key={row.key}>
                  <div className={`bg-[#F9FAF9] border-r border-[#D7DBD9] flex items-center py-[4px] px-[8px] ${rowIndex === 0 ? '' : 'border-t'}`}>
                    <span className="font-['Public_Sans'] font-medium text-xs leading-[16px] text-slate-900">{row.label}</span>
                  </div>
                  <div className={`bg-white flex items-center justify-end py-[4px] px-[8px] ${rowIndex === 0 ? '' : 'border-t border-[#D7DBD9]'}`}>
                    <span className={`font-['Public_Sans'] font-normal text-xs leading-[16px] text-slate-900 ${row.emphasis ? 'font-semibold' : ''}`}>
                      {row.value}
                    </span>
                  </div>
                </React.Fragment>
              ))}
            </div>
            <div className="flex flex-wrap gap-2">
              <ExportDropdown
                onExportPDF={handleClientDownloadPDF}
                onExportExcel={handleClientDownloadExcel}
                disabled={downloading}
              />
              {onSendClick && (
                <Button variant="primary" onClick={onSendClick}>
                  Send
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ClientViewPopup;
